import dot from 'dot-object';
import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '../../../common/functions/cn';
import useTestTag from '../../../common/hooks/use-test-tag';
import type { MakeRequired } from '../../../common/types/make-required';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import { Field, FieldLabel } from '../../field/field';
import type { JsonData, JsonDataItem } from '../../generic-types';
import { Checkbox } from '../checkbox/checkbox';
import SelectButton from '../select-button/select-button.component';

export interface CheckboxGroupResult {
  value: unknown;
  text: string;
  color?: string;
}

export interface CheckBoxGroupProps
  extends Omit<
    MakeRequired<DynamicFieldComponentProps<Array<CheckboxGroupResult>>, 'name'>,
    'label' | 'labelStyles' | 'error' | 'required'
  > {
  data?: JsonData;
  textField: string;
  valueField: string;
  className?: string;
  multiSelectCheckboxType?: 'buttons' | 'checkboxes';
  checkboxGroupStyling?: string;
  archivedField?: string;
  showSelectedArchivedOptions?: boolean; // Feature flag to show selected archived options works in combination with archivedField
  'aria-invalid'?: boolean;
}

const CheckboxGroup = ({
  name,
  value,
  inputRef,
  data,
  textField,
  valueField,
  readOnly,
  onChange,
  disabled,
  className,
  multiSelectCheckboxType = 'checkboxes',
  checkboxGroupStyling,
  archivedField,
  showSelectedArchivedOptions = false,
  'aria-invalid': ariaInvalid,
}: CheckBoxGroupProps) => {
  const [selectedValues, setSelectedValues] = useState<Array<CheckboxGroupResult>>([]);
  const initialSelectedValues = useRef<Array<CheckboxGroupResult>>([]);

  const isCheckBox = multiSelectCheckboxType === 'checkboxes';

  useEffect(() => {
    const newValues = Array.isArray(value) ? value : [];
    setSelectedValues(newValues);
    if (initialSelectedValues.current.length === 0 && newValues.length > 0) {
      initialSelectedValues.current = newValues;
    }
  }, [value]);

  const tagCreator = useTestTag(name);
  const itemIdPrefix = useId();

  const isSelected = (item: JsonDataItem): boolean => {
    return Array.isArray(selectedValues) && selectedValues.some((_item) => _item.value == item[valueField]);
  };

  const wasInitiallySelected = (item: JsonDataItem): boolean => {
    return initialSelectedValues.current.some((_item) => _item.value == item[valueField]);
  };

  const shouldRenderOption = (item: JsonDataItem): boolean => {
    if (!showSelectedArchivedOptions || !archivedField) {
      return true;
    } else {
      const isArchived = !!dot.pick(archivedField, item);

      if (!isArchived) {
        return true;
      } else {
        return wasInitiallySelected(item);
      }
    }
  };

  const handleChange = (checked: boolean, dataItem: JsonDataItem) => {
    const value = dataItem[valueField];

    let update: Array<CheckboxGroupResult>;
    if (checked) {
      update = [...selectedValues, { value: value, text: dataItem[textField] as string }];
    } else {
      update = selectedValues.filter((item) => item.value != value);
    }

    setSelectedValues(update);
    onChange?.(update);
  };

  const renderItem = (item: JsonDataItem, i: number) => {
    const itemId = `${itemIdPrefix}-${i}`;

    return (
      <Field key={`${name}_item_${i}`} orientation="horizontal" data-disabled={disabled}>
        <Checkbox
          id={itemId}
          name={name}
          checked={isSelected(item)}
          onChange={(event) => handleChange(event.target.checked, item)}
          readOnly={readOnly}
          aria-invalid={ariaInvalid || undefined}
          disabled={disabled}
        />
        <FieldLabel htmlFor={itemId} className="font-normal">
          {item[textField] as string}
        </FieldLabel>
      </Field>
    );
  };

  const renderItems = (finalData?: JsonData) => {
    if (!finalData) return null;
    return finalData.filter(shouldRenderOption).map(renderItem);
  };

  const renderSelectButtons = (finalData?: JsonData) => {
    if (!finalData) return null;
    const filteredData = finalData.filter(shouldRenderOption);

    return (
      <SelectButton
        id={name}
        textField={textField}
        valueField={valueField}
        inputRef={inputRef}
        value={selectedValues || []}
        data={filteredData}
        onChange={onChange}
        readOnly={readOnly}
        disabled={disabled}
      />
    );
  };

  return (
    <div
      data-slot="checkbox-group"
      className={cn(
        'flex flex-col gap-2',
        checkboxGroupStyling,
        // SelectButton has no per-item invalid styling, so the buttons variant marks the group instead.
        ariaInvalid && !readOnly && !isCheckBox && 'rounded-md border border-error',
        className
      )}
      ref={tagCreator('checkbox-group')}
    >
      {isCheckBox ? renderItems(data) : renderSelectButtons(data)}
    </div>
  );
};

export default CheckboxGroup;
